export const MINUTE = 60_000;
export const HOUR = 60 * MINUTE;
export const DAY = 24 * HOUR;

const absoluteFormat = new Intl.DateTimeFormat(undefined, {
  month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
});
const dayFormat = new Intl.DateTimeFormat(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
const clockFormat = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit', second: '2-digit', timeZoneName: 'short' });

/** Precise, timezone-qualified time for tooltips and anything an operator may act on. */
export const absolute = (value: string | Date) => absoluteFormat.format(new Date(value));
export const longDay = (value: Date) => dayFormat.format(value);
export const clock = (value: string | Date) => clockFormat.format(new Date(value));
export const timeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

/** Elapsed time in plain words: "4 min", "3 hours", "9 days". */
export function duration(ms: number) {
  const abs = Math.max(0, ms);
  const unit = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
  if (abs < MINUTE) return 'under a minute';
  if (abs < HOUR) return unit(Math.floor(abs / MINUTE), 'min').replace('mins', 'min');
  if (abs < DAY) return unit(Math.floor(abs / HOUR), 'hour');
  if (abs < 14 * DAY) return unit(Math.floor(abs / DAY), 'day');
  if (abs < 60 * DAY) return unit(Math.floor(abs / (7 * DAY)), 'week');
  return unit(Math.floor(abs / (30 * DAY)), 'month');
}

export function ago(value: string | Date, now = Date.now()) {
  const ms = now - new Date(value).getTime();
  if (ms < -MINUTE) return `in ${duration(-ms)}`; // Clock skew: never pretend the future is the past.
  if (ms < MINUTE) return 'just now';
  return `${duration(ms)} ago`;
}

/** Compact age for dense tables: 4m, 3h, 9d, 5w. */
export function shortAgo(value: string | Date, now = Date.now()) {
  const ms = Math.max(0, now - new Date(value).getTime());
  if (ms < MINUTE) return 'now';
  if (ms < HOUR) return `${Math.floor(ms / MINUTE)}m`;
  if (ms < DAY) return `${Math.floor(ms / HOUR)}h`;
  if (ms < 14 * DAY) return `${Math.floor(ms / DAY)}d`;
  if (ms < 60 * DAY) return `${Math.floor(ms / (7 * DAY))}w`;
  return `${Math.floor(ms / (30 * DAY))}mo`;
}

export type Freshness = 'current' | 'recent' | 'quiet' | 'stale' | 'never';
/** How much a check-in time can be trusted to describe the device right now. */
export function freshness(value: string | null, now = Date.now()): Freshness {
  if (!value) return 'never';
  const ms = now - new Date(value).getTime();
  if (ms < HOUR) return 'current';
  if (ms < DAY) return 'recent';
  if (ms < 7 * DAY) return 'quiet';
  return 'stale';
}

/** Round down so relative filters produce stable query keys within a minute. */
export const minuteFloor = (now = Date.now()) => Math.floor(now / MINUTE) * MINUTE;
